import { useCallback, useEffect, useState } from "react";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import VisitsTable from "@/components/portal/VisitsTable";
import VisitDialog from "@/components/portal/VisitDialog";
import { PageHeader, Spinner, ConfirmDialog, btnPrimary, inputCls, STATUS, VISIT_STATUSES } from "@/components/portal/ui";

const SCOPES = [
  { key: "upcoming", label: "Prossime" },
  { key: "past", label: "Passate" },
  { key: "all", label: "Tutte" },
];

export default function AdminVisits() {
  const [visits, setVisits] = useState(null);
  const [specialties, setSpecialties] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [nurses, setNurses] = useState([]);
  const [scope, setScope] = useState("upcoming");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState({ open: false, visit: null });
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    const today = new Date().toISOString().slice(0, 10);
    const params = {};
    if (scope === "upcoming") params.date_from = today;
    if (scope === "past") params.date_to = today;
    if (status) params.status = status;
    if (q.trim()) params.q = q.trim();
    try {
      const { data } = await api.get("/visits", { params });
      setVisits(scope === "past" ? [...data].reverse() : data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [scope, status, q]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    Promise.all([api.get("/specialties"), api.get("/doctors"), api.get("/nurses")]).then(([s, d, n]) => {
      setSpecialties(s.data);
      setDoctors(d.data);
      setNurses(n.data);
    });
  }, []);

  const changeStatus = async (v, newStatus) => {
    try {
      await api.put(`/visits/${v.id}`, { status: newStatus });
      toast.success(`Visita ${STATUS[newStatus].label.toLowerCase()}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/visits/${toDelete.id}`);
      toast.success("Visita eliminata");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="admin-visits">
      <PageHeader
        eyebrow="Visite"
        title="Gestione visite"
        desc="Programma le visite negli orari liberi dei medici, assegna l'infermiere e segui lo stato."
        actions={
          <button onClick={() => setDialog({ open: true, visit: null })} className={btnPrimary} data-testid="new-visit-btn">
            <Plus className="h-4 w-4" /> Nuova visita
          </button>
        }
      />

      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="inline-flex rounded-full bg-white p-1 ring-1 ring-[#5B2C91]/10" data-testid="visits-scope-tabs">
          {SCOPES.map((s) => (
            <button key={s.key} onClick={() => setScope(s.key)} className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${scope === s.key ? "bg-[#5B2C91] text-white" : "text-[#4A4059] hover:text-[#5B2C91]"}`} data-testid={`visits-scope-${s.key}`}>
              {s.label}
            </button>
          ))}
        </div>
        <div className="flex flex-1 gap-2 lg:max-w-xl">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8A809A]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca paziente o telefono…" className={`${inputCls} !pl-10`} data-testid="visits-search-input" />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} !w-auto`} data-testid="visits-status-filter">
            <option value="">Tutti gli stati</option>
            {VISIT_STATUSES.map((s) => (
              <option key={s} value={s}>{STATUS[s].label}</option>
            ))}
          </select>
        </div>
      </div>

      {visits === null ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : (
        <VisitsTable visits={visits} role="admin" onEdit={(v) => setDialog({ open: true, visit: v })} onDelete={setToDelete} onStatus={changeStatus} emptyDesc="Nessuna visita corrisponde ai filtri selezionati." />
      )}

      <VisitDialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} visit={dialog.visit} specialties={specialties} doctors={doctors} nurses={nurses} onSaved={load} />
      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} title="Eliminare la visita?" desc={toDelete ? `${toDelete.patient_name} · ${toDelete.date} ${toDelete.start_time}` : ""} onConfirm={remove} confirmLabel="Elimina" destructive />
    </div>
  );
}
